import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import style from './Testimonials.module.scss';

const PAGES = [
    [
        { quote: '"Yesterday Stories is a user-friendly place to find local short histories where past characters and events come to life. Locals and visitors have a new, easy to use, information resource. Yesterday Stories mobile phone app uses a location list and map to allow everyone to “get the good oil” on places and people of interest. The hearing impaired can often read and the sight impaired can hear the stories of the community. I can thoroughly recommend this app as it is becoming a valuable resource for investigating the history of people and things wherever you happen to be. Both professional and amateur video makers are contributing to Yesterday Stories. I have contributed and so can you."', attr: '— John Devenish' },
        { quote: '"My eight year old son doesn\'t like watching TV and instead prefers streaming YouTube on the TV. The other night I got sick of watching silly meme videos so I cast a wider net with a search and the Yesterday Stories video on Young came up and we watched it together. He got right into it, and has been asking me since when I am taking him to pick some cherries. The Croatian angle in the story was also fascinating as my wife is of Croatian background, so Young is definitely on our list for a short holiday when we can travel again."', attr: '— MC' },
        { quote: '"I have lived here all my life and I had no idea about the history of my area. This is just fantastic"', attr: '— Yesterday stories User' },
    ],
    [
        { quote: '"I love what you are doing, it’s such a gap in the market, and so authentic."', attr: '- Business and Investment Attraction Officer (Tourism)' },
        { quote: '"I love the app. It’s an amazing way to understand the history of the places we travel to. The stories add to our experience and Ishare it with my children to bring that story to life."', attr: '— Mel' },
        { quote: '"I think the app is awesome! Big fan of it, definitely will be using it a fair bit. I run past the Wollongong Entertainment Centre every day and never knew it was an old burial ground!"', attr: '— Daniel' },
    ],
];

const QuoteCard = ({ quote, attr }) => {
    const [expanded, setExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const quoteRef = useRef(null);
    const firstRun = useRef(true);

    // Does the quote overflow the clamp? (only meaningful while collapsed)
    useEffect(() => {
        const el = quoteRef.current;
        if (!el || expanded) return undefined;
        const measure = () => setIsTruncated(el.scrollHeight - el.clientHeight > 1);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [expanded, quote]);

    // Smoothly animate the clamp open/closed. After expanding we release the
    // max-height cap entirely (`none`) so long quotes can never be clipped by a
    // slightly-off measurement (e.g. taken before the web font settles).
    useLayoutEffect(() => {
        const el = quoteRef.current;
        if (!el) return undefined;
        if (firstRun.current) {
            firstRun.current = false;
            el.style.maxHeight = expanded ? 'none' : '';
            return undefined;
        }
        if (expanded) {
            el.style.maxHeight = `${el.scrollHeight}px`;
            const release = () => { el.style.maxHeight = 'none'; };
            el.addEventListener('transitionend', release, { once: true });
            return () => el.removeEventListener('transitionend', release);
        }
        // Collapsing: lock the current full height, then animate down to the clamp.
        el.style.maxHeight = `${el.scrollHeight}px`;
        void el.offsetHeight; // force reflow so the start height registers
        el.style.maxHeight = '';
        return undefined;
    }, [expanded]);

    return (
        <div className={style.quoteCard}>
            <blockquote
                ref={quoteRef}
                className={`${style.quoteText} ${expanded ? style.quoteExpanded : ''}`}
            >
                {quote}
            </blockquote>
            <button
                type="button"
                className={style.readMore}
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
                // reserve the line so every card keeps the same height; hide (and drop
                // from tab order) on quotes that fit and have nothing to expand
                style={!isTruncated && !expanded ? { visibility: 'hidden' } : undefined}
            >
                {expanded ? 'Show less' : 'Read more'}
            </button>
            <div className={style.quoteAttr}>{attr}</div>
        </div>
    );
};

const Testimonials = () => {
    const [page, setPage] = useState(0);
    const total = PAGES.length;

    return (
        <section className={style.quoteBand}>
            <div className="wrap">
                <div className="section-head">
                    <div className="eyebrow">What people are saying</div>
                    <h2>Testimonial from the people who've used it.</h2>
                </div>
                <div className={style.quoteCarousel}>
                    <button
                        className={style.quoteArrow}
                        aria-label="Previous testimonials"
                        disabled={page === 0}
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <div className={style.quoteTrackWrap}>
                        <div className={style.quoteTrack} style={{ transform: `translateX(-${page * 100}%)` }}>
                            {PAGES.map((quotes, i) => (
                                <div className={style.quotePage} key={i}>
                                    {quotes.map(q => (
                                        <QuoteCard quote={q.quote} attr={q.attr} key={q.attr} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <button
                        className={style.quoteArrow}
                        aria-label="Next testimonials"
                        disabled={page === total - 1}
                        onClick={() => setPage(p => Math.min(total - 1, p + 1))}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3 11 8l-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                </div>
                <div className={style.quoteDots}>
                    {PAGES.map((_, i) => (
                        <button
                            key={i}
                            className={`${style.dot} ${i === page ? style.active : ''}`}
                            aria-label={`Go to testimonials page ${i + 1}`}
                            onClick={() => setPage(i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
